/**
 * 4_AsdDDFormulation.js — Final Formulation (step 4 of the Adult Autism DD)
 *
 * The closing step of the Diagnostic Decision module. It shows a read-only
 * summary of the Autism decision and the Other Conditions decisions, then gives
 * the clinician a free-text box to write the final formulation that ties it all
 * together.
 *
 * On "Complete Diagnostic Decision" we first open a confirmation dialog
 * (same ModuleCompletionDialog pattern as TOC ADHD Final Formulation / Autism
 * SSI Review) with "Go back and edit" vs "Generate Report". Only Generate
 * Report then:
 *   1. flushes the final-formulation write,
 *   2. marks assessmentStages/diagnosticDecision as completed (status,
 *      progress=1, audit fields) — which also unlocks PAD,
 *   3. triggers Formulation (+ Diagnostic Decision / Secondary Diagnoses)
 *      section regeneration, and
 *   4. shows the generation progress dialog, then navigates to documents.
 *
 * The final-formulation text is persisted via the debounced updateAssessmentStage
 * API (on blur and on navigation), using a lazy initial state so context
 * refreshes never clobber what the clinician is typing.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/solid';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import ModularSideNavbar from '../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import StructuredNotesEditor, { normaliseStructuredNotes } from '../12_observation/components/StructuredNotesEditor';
import NotInReportNotesPanel from '../../../components/autismReview/NotInReportNotesPanel';
import ConditionSelectionPanelTOC from '../6_clinicalInterview/components/ConditionSelectionPanelTOC';
import { ReadOnlyAutoResizeTextarea } from '../../../components/AutoResizeTextarea';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import ModuleCompletionDialog, {
  CancelButton,
  GenerateReportButton,
  DialogMessage,
  SummarySection,
} from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useModularSectionExitRegeneration } from '../../../hooks/useModularSectionExitRegeneration';
import { useDragAutoScroll } from '../../../hooks/useDragAutoScroll';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { asdLog, summariseNotes } from '../../../utils/autismDebugLog';
import { normaliseNotesValue } from '../../../utils/autismReviewData';
import { makeNoteId } from '../../../utils/structuredNotesMove';
import { auth } from '../../../firebase';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstCompletedFields } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import {
  getAutismDDStepRoutes,
  getCombinedEmergingDifferentials,
  buildAutismDDNavItems,
  getAutismDDRouteForSectionIndex,
  computeAutismDDProgress,
  isOtherConditionsComplete,
  isFinalFormulationComplete,
} from '../../../utils/autismDDRouting';
import { ASD_CONDITION_LABEL } from '../../../constants/autismConditions';

const DECISION_BADGE_STYLES = {
  Met: 'bg-green-100 text-green-800 border-green-200',
  'Not met': 'bg-red-100 text-red-700 border-red-200',
  'Provide next steps': 'bg-blue-100 text-blue-800 border-blue-200',
  'Rule out': 'bg-red-100 text-red-700 border-red-200',
};

function DecisionBadge({ decision }) {
  const style = DECISION_BADGE_STYLES[decision] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${style}`}>
      {decision}
    </span>
  );
}

/** Read-only Yes/No chip — same emerald/red palette as the Decision step toggles. */
function CriterionMetBadge({ met }) {
  const style = met
    ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
    : 'bg-red-100 text-red-700 border-red-300';
  return (
    <span className={`inline-block min-w-[2.75rem] px-2.5 py-0.5 rounded-full text-xs font-semibold border text-center ${style}`}>
      {met ? 'Yes' : 'No'}
    </span>
  );
}

const CRITERION_LABELS = {
  A1: 'A1 — Social-Emotional Reciprocity',
  A2: 'A2 — Non-Verbal Communication',
  A3: 'A3 — Relationships',
  B1: 'B1 — Motor Movements / Use of Objects / Speech',
  B2: 'B2 — Sameness / Routines',
  B3: 'B3 — Focused Interests',
  B4: 'B4 — Sensory',
  C: 'C — Early Developmental Period',
  D: 'D — Functional Impairment',
  E: 'E — Not Better Explained',
};

/** Domain ids for the read-only emerging formulation panels (must match MDT Review). */
const SSI_EMERGING_DOMAIN = 'ssiEmergingFormulationReadOnly';
const OBS_EMERGING_DOMAIN = 'obsEmergingFormulationReadOnly';

/**
 * Side-by-side row layout — same pattern as the MDT Review page. When
 * `observationEnabled` is false (orgs like LMP with no Behavioural Observation
 * appointment) the right-hand Observation panel is dropped and the interview
 * panel spans the full width. Defaults to two columns, so TOC is unaffected.
 */
function ReviewRow({ title, left, right, observationEnabled = true }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">{title}</h2>
      <div className={`grid grid-cols-1 ${observationEnabled ? 'lg:grid-cols-2' : ''} gap-6 items-start`}>
        <div>{left}</div>
        {observationEnabled && <div>{right}</div>}
      </div>
    </div>
  );
}

export default function AsdDDFormulation() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();

  const {
    header,
    assessmentStages,
    reportContent,
    updateAssessmentStage,
    flushStageWrites,
  } = useContext(ModularPatientDataContext);

  const isPreviewMode = useIsPreviewMode();
  const canEdit = useCanEditStage(header || {}, 'DD', { previewMode: isPreviewMode });

  // Whether this patient's workflow runs Behavioural Observation. When disabled
  // (e.g. LMP) the emerging-formulation row collapses to the interview panel
  // only. Absent -> enabled, so TOC keeps the side-by-side layout.
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);
  const isObservationEnabled = wf?.isObservationEnabled !== false;

  const ddStage = assessmentStages?.diagnosticDecision;
  const autismDecision = ddStage?.data?.autismDecision || {};
  const criteria = autismDecision.criteria || {};
  const outcome = autismDecision.outcome || null;

  const union = useMemo(
    () => getCombinedEmergingDifferentials(assessmentStages),
    [assessmentStages],
  );

  // Per-appointment condition lists — used by the stacked differentials panels
  // in the skip-differentials-decisions flow (TOC) so the clinician sees which
  // appointment identified which conditions, matching the MDT Review layout.
  const ssiConditions = useMemo(
    () => (assessmentStages?.semiStructuredInterview?.review?.formulationConditions || [])
      .filter(c => c !== ASD_CONDITION_LABEL),
    [assessmentStages],
  );
  const obsConditions = useMemo(
    () => (assessmentStages?.observation?.review?.formulationConditions || [])
      .filter(c => c !== ASD_CONDITION_LABEL),
    [assessmentStages],
  );

  // When skipAutismDifferentialsDecisions is true (TOC), the Other Conditions
  // step does not exist — emerging differentials are shown as a read-only list
  // on this page instead of decision badges.
  const hasOther = !wf.skipAutismDifferentialsDecisions && union.length > 0;

  // Detect historical decisions — these exist for patients whose DD was
  // completed BEFORE differentials decisions were disabled. Preserved
  // as a read-only panel for historical record-keeping.
  const hasHistoricalDecisions = Object.keys(
    ddStage?.data?.otherConditions?.finalComorbidDiagnoses || {}
  ).length > 0;

  const otherConditionDecisions = useMemo(() => {
    const fcd = ddStage?.data?.otherConditions?.finalComorbidDiagnoses || {};
    return [...union].sort().map((name) => ({
      name,
      decision: fcd[name]?.decision || 'No decision',
      notes: fcd[name]?.notes || '',
    }));
  }, [ddStage, union]);

  // Emerging formulations from the interview and observation appointments — read
  // only here; clinicians drag individual note cards into the final box below.
  const ssiEmergingNotes = useMemo(
    () => normaliseNotesValue(assessmentStages?.semiStructuredInterview?.review?.emergingFormulation),
    [assessmentStages],
  );
  const obsEmergingNotes = useMemo(
    () => normaliseNotesValue(assessmentStages?.observation?.review?.emergingFormulation),
    [assessmentStages],
  );

  // Lazy initial state — never clobbered by later context refreshes.
  // The final formulation is now a structured-notes array (one note per
  // bullet), matching every other Autism free-text field. Legacy plain-string
  // values are intentionally NOT migrated (per the agreed design decision).
  const [finalFormulationNotes, setFinalFormulationNotes] = useState(
    () => normaliseStructuredNotes(ddStage?.data?.finalFormulationNotes),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isCompletingRef = useRef(false);

  // ── Dirty tracking ───────────────────────────────────────────────────────────
  const [hasChanges, setHasChanges] = useState(false);

  // ── Auto-scroll while dragging notes near the scroll container edge ────────
  const contentRef = useRef(null);
  useDragAutoScroll(contentRef);

  // ── Status promotion ────────────────────────────────────────────────────────
  const hasStartedRef = useRef(false);
  const maybePromoteStatus = (updates) => {
    if (!hasStartedRef.current && ddStage?.status === 'notStarted') {
      hasStartedRef.current = true;
      return { ...updates, status: 'inProgress' };
    }
    return updates;
  };

  // Persist the formulation notes + recomputed progress. The StructuredNotesEditor
  // commits each note discretely (on Enter), so we persist on every change rather
  // than on blur.
  const persistFormulation = (notes) => {
    if (!canEdit) return;
    const value = notes !== undefined ? notes : finalFormulationNotes;
    const nextData = { ...(ddStage?.data || {}), finalFormulationNotes: value };
    let progress = computeAutismDDProgress({ data: nextData, union, status: ddStage?.status });
    if (ddStage?.status === 'completed' && progress < 1) progress = 1;
    updateAssessmentStage('diagnosticDecision', maybePromoteStatus({
      'data.finalFormulationNotes': value,
      progress,
    }));
  };

  const handleFormulationChange = useCallback((notes) => {
    setFinalFormulationNotes(notes);
    setHasChanges(true);
    persistFormulation(notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, ddStage, union]);

  /**
   * Copy a note card from a read-only emerging formulation panel into the
   * editable final formulation box. The source appointment records are left
   * untouched — this is a copy, not a move (same semantics as OBS master→criterion).
   *
   * Uses functional setState so two rapid successive drops cannot lose the
   * first copy (each updater sees the previous state, not a stale closure).
   */
  const handleCopyEmergingNoteIntoFinal = (sourceDomainId, noteId, _targetDomainId, insertAt) => {
    if (!canEdit) return;

    const sourceNotes =
      sourceDomainId === SSI_EMERGING_DOMAIN ? ssiEmergingNotes :
      sourceDomainId === OBS_EMERGING_DOMAIN ? obsEmergingNotes :
      null;
    if (!sourceNotes) return;

    const note = sourceNotes.find((n) => n && n.id === noteId);
    if (!note || !String(note.text || '').trim()) return;

    const copy = { ...note, id: makeNoteId() };
    setFinalFormulationNotes((prev) => {
      const next = [...prev];
      const idx = insertAt != null && insertAt >= 0 && insertAt <= next.length
        ? insertAt
        : next.length;
      next.splice(idx, 0, copy);
      persistFormulation(next);
      return next;
    });
    setHasChanges(true);
  };

  // Unmount safety-net — persist the latest notes if the clinician leaves via an
  // unmanaged exit (breadcrumb, top nav, browser back) mid-edit. This is a
  // separate concern from generation (the exit-regen hook handles that).
  const latestRef = useRef(finalFormulationNotes);
  useEffect(() => { latestRef.current = finalFormulationNotes; }, [finalFormulationNotes]);
  const canEditRef = useRef(canEdit);
  useEffect(() => { canEditRef.current = canEdit; }, [canEdit]);
  useEffect(() => () => {
    if (!canEditRef.current) return;
    const latest = latestRef.current;
    if (Array.isArray(latest) && latest.length > 0) {
      updateAssessmentStage('diagnosticDecision', { 'data.finalFormulationNotes': latest });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every other condition must be decided before the module can be completed.
  // The other-conditions Next is already gated, but a clinician can reach this
  // page via the sidebar (Final Formulation unlocks as soon as the Autism
  // decision is valid), so this is the backstop that guarantees no condition is
  // left undecided / silently dropped from the report.
  const otherComplete = useMemo(
    () => isOtherConditionsComplete(ddStage?.data?.otherConditions, union),
    [ddStage, union],
  );

  const hasFormulation = isFinalFormulationComplete(finalFormulationNotes);
  // When skipAutismDifferentialsDecisions is true, otherComplete is not
  // required because the Other Conditions step does not exist in this workflow.
  const completeBlocked = !hasFormulation || (!wf.skipAutismDifferentialsDecisions && hasOther && !otherComplete);
  const isValid = !completeBlocked;

  // ── Exit-regeneration via shared hook ────────────────────────────────────────
  // shouldRegenerate is gated on hasFormulation (at least one note) so that an
  // empty formulation box does not draft a vacuous paragraph on Back. This is
  // independent of isValid (which also requires otherComplete).
  const { markRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: 'diagnosticDecision',
    sectionTitle: 'Formulation',
    hasChanges,
    canEdit,
    reportContent,
    shouldRegenerate: hasFormulation,
  });

  // ── Managed-exit flush + generate ──────────────────────────────────────────
  // Back and sidebar navigation trigger Formulation generation with a flush
  // promise so the backend reads committed data. markRegenHandled suppresses
  // the hook's unmount net so we never double-fire.
  const flushAndGenerateFormulation = async () => {
    markRegenHandled();
    persistFormulation();
    const dataReady = flushStageWrites('diagnosticDecision');
    const generated = reportContent?.diagnosticDecision?.stageSectionsGenerated || [];
    if (canEdit && hasFormulation && (hasChanges || !generated.includes('Formulation'))) {
      TriggerGenerateDocumentSection(
        patientId, 'Autism Diagnostic Decision', 'Formulation', [], dataReady,
      );
    }
    await dataReady;
  };

  const currentSectionIndex = hasOther ? 3 : 2;
  const { items: navItems, orderedIds } = useMemo(
    () => buildAutismDDNavItems({ assessmentStages, currentSectionIndex, skipDifferentialsDecisions: wf.skipAutismDifferentialsDecisions }),
    [assessmentStages, currentSectionIndex, wf.skipAutismDifferentialsDecisions],
  );

  const navigateToSection = async (index) => {
    await flushAndGenerateFormulation();
    navigate(getAutismDDRouteForSectionIndex(patientId, index, hasOther));
  };

  const handleBack = async () => {
    await flushAndGenerateFormulation();
    const routes = getAutismDDStepRoutes(patientId);
    navigate(hasOther ? routes.otherConditions : routes.autismDecision);
  };

  /**
   * Opens the confirmation dialog after flushing the latest formulation write.
   * Does NOT mark the stage complete — that only happens when the clinician
   * confirms via "Generate Report" (so "Go back and edit" is a true no-op).
   */
  const handleComplete = async () => {
    if (!canEdit || !isValid) {
      asdLog('DD', 'complete blocked', { canEdit, isValid });
      return;
    }
    persistFormulation();
    await flushStageWrites('diagnosticDecision');
    setIsDialogOpen(true);
  };

  /**
   * Confirmed completion: marks the DD stage complete, regenerates report
   * sections, then shows the generation progress dialog.
   */
  const handleGenerateReport = async () => {
    if (!canEdit || !isValid || isCompletingRef.current) {
      asdLog('DD', 'generate blocked', { canEdit, isValid, inFlight: isCompletingRef.current });
      return;
    }
    isCompletingRef.current = true;
    markRegenHandled();
    asdLog('DD', 'complete: marking stage completed', {
      patientId, outcome, hasOther,
      formulation: summariseNotes(finalFormulationNotes),
    });
    setIsDialogOpen(false);
    setIsSaving(true);

    // Mark complete with audit metadata. Forcing progress to 1 here is the
    // single source of the "100%" dashboard pill.
    //
    // firstCompletedAt (+By/ByName) is write-once via buildFirstCompletedFields
    // — the same helper the ADHD DD pages use — so a re-completed DD keeps its
    // original date while lastCompletedAt moves. The Patient Overview shows
    // firstCompletedAt as the DD card's "estimated" appointment date.
    const ddFirstCompleted = buildFirstCompletedFields(assessmentStages?.diagnosticDecision);
    updateAssessmentStage('diagnosticDecision', {
      'data.finalFormulationNotes': finalFormulationNotes,
      status: 'completed',
      progress: 1,
      lastCompletedAt: serverTimestamp(),
      lastCompletedBy: auth.currentUser?.uid,
      lastCompletedByName: auth.currentUser?.displayName,
      ...ddFirstCompleted,
    });

    // Inferred appointment-date audit entry on the first completion only
    // (fire-and-forget; appointmentDateHistory subcollection, never
    // appointmentDates itself). `dd` is the DD card's short key.
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'dd',
      didWriteFirstCompleted: Object.keys(ddFirstCompleted).length > 0,
      enabled: wf.isAppointmentDatesEnabled,
    });

    const dataReady = flushStageWrites('diagnosticDecision');

    // Regenerate every DD report section on completion. We intentionally trigger
    // all sections (not just ungenerated ones): the backend's
    // should_regenerate_paragraph dedupes unchanged paragraphs, so this is the
    // robust way to capture any edit made since the section was last generated —
    // including edits the clinician made and then left via an unmanaged exit.
    asdLog('DD', 'complete: triggering section regeneration', {
      diagnosticDecision: Boolean(outcome), secondaryDiagnoses: hasOther, formulation: true,
    });
    if (outcome) {
      TriggerGenerateDocumentSection(patientId, 'Autism Diagnostic Decision', 'Diagnostic Decision', [], dataReady);
    }
    if (hasOther) {
      TriggerGenerateDocumentSection(patientId, 'Autism Diagnostic Decision', 'Secondary Diagnoses', [], dataReady);
    }
    TriggerGenerateDocumentSection(patientId, 'Autism Diagnostic Decision', 'Formulation', [], dataReady);

    await dataReady;
    setIsSaving(false);
    setIsGenerating(true);
  };

  const handleAfterGeneration = () => {
    navigate(`/patient/${patientId}/adult-autism/documents`);
  };

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Diagnostic Decision', getAutismDDStepRoutes(patientId).mdtReview],
    ['Final Formulation'],
  ];

  const decidedCriteria = Object.keys(CRITERION_LABELS).filter((id) => criteria[id] !== undefined && criteria[id] !== null);

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* Wizard shell — shared sidebar + scrollable content + fixed footer. */}
      <div className="flex h-[calc(100vh-8rem)]">
        <ModularSideNavbar
          items={navItems}
          activeId="finalFormulation"
          onSelect={(id) => navigateToSection(orderedIds.indexOf(id))}
        />

        <div className="flex-1 flex flex-col min-h-0">
          {/* Preview mode info banner — shown when opened via a Preview button */}
          {isPreviewMode && <PreviewModeBanner />}
          <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Final Formulation</h1>
          </div>

          {/* Read-only decision summaries */}
          {(() => {
            // Determine which variant of Panel 2 to show (mirrors ADHD 3_Formulation.js logic)
            const showStandardPanel2 = hasOther;
            const showHistoricalPanel2 = wf.skipAutismDifferentialsDecisions && hasHistoricalDecisions;
            const showEmergingPanel2 = wf.skipAutismDifferentialsDecisions && !hasHistoricalDecisions && union.length > 0;
            const showPanel2 = showStandardPanel2 || showHistoricalPanel2 || showEmergingPanel2;
            return (
              <div className={`grid gap-6 mb-6 ${showPanel2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                {/* Decision for Autism */}
                <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left">
                  <h3 className="text-lg font-semibold mb-3">Decision for Autism</h3>
                  <p className="text-sm font-semibold text-gray-800 mb-1.5">Autism Spectrum Disorder (ASD)</p>
                  {outcome ? (
                    <div className="mb-3"><DecisionBadge decision={outcome} /></div>
                  ) : (
                    <p className="text-sm text-gray-400 italic mb-3">No outcome recorded yet.</p>
                  )}
                  {/* Compact two-column table — easier to scan than "Label: Yes" lines,
                      especially when every criterion is filled in. */}
                  {decidedCriteria.length > 0 && (
                    <div className="w-full overflow-x-auto rounded-lg border border-gray-200 bg-gray-100">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-300 text-left text-xs uppercase tracking-wide text-gray-500">
                            <th scope="col" className="px-3 py-2 font-semibold">Criterion</th>
                            <th scope="col" className="px-3 py-2 font-semibold text-right w-20">Met</th>
                          </tr>
                        </thead>
                        <tbody>
                          {decidedCriteria.map((id) => (
                            <tr key={id} className="border-b border-gray-200 last:border-0">
                              <td className="px-3 py-1.5 text-gray-800">{CRITERION_LABELS[id]}</td>
                              <td className="px-3 py-1.5 text-right">
                                <CriterionMetBadge met={Boolean(criteria[id])} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Panel 2a: Standard — Decisions for Other Conditions (non-skip orgs) */}
                {showStandardPanel2 && (
                  <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left">
                    <h3 className="text-lg font-semibold mb-3">Decisions for Other Conditions</h3>
                    {hasOther && !otherComplete && (
                      <p className="text-xs text-red-600 mb-3">
                        Decide every condition before completing the Diagnostic Decision.
                      </p>
                    )}
                    <div className="space-y-5">
                      {otherConditionDecisions.map((cond) => (
                        <div key={cond.name}>
                          <p className="text-sm font-semibold text-gray-800 mb-1.5">{cond.name}</p>
                          <div className="mb-2"><DecisionBadge decision={cond.decision} /></div>
                          {cond.notes && (
                            <ReadOnlyAutoResizeTextarea
                              value={cond.notes}
                              className="w-full p-3 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-700 resize-none cursor-not-allowed opacity-75"
                              ariaLabel={`Notes for ${cond.name}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Panel 2b: Historical — preserved decisions from before the change */}
                {showHistoricalPanel2 && (
                  <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left">
                    <h3 className="text-lg font-semibold mb-3">Decision on Differentials (Historical)</h3>
                    <div className="space-y-5">
                      {otherConditionDecisions.map((cond) => (
                        <div key={cond.name}>
                          <p className="text-sm font-semibold text-gray-800 mb-1.5">{cond.name}</p>
                          <div className="mb-2"><DecisionBadge decision={cond.decision} /></div>
                          {cond.notes && (
                            <ReadOnlyAutoResizeTextarea
                              value={cond.notes}
                              className="w-full p-3 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-700 resize-none cursor-not-allowed opacity-75"
                              ariaLabel={`Notes for ${cond.name}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Panel 2c: Read-only emerging differentials — per-appointment
                    panels stacked vertically. Unlike the MDT Review page (which
                    uses side-by-side columns), Final Formulation stacks them so
                    the clinician can see both appointments' differentials without
                    the decision panel competing for horizontal space. Uses the
                    same ConditionSelectionPanelTOC component as MDT Review. */}
                {showEmergingPanel2 && (
                  <div className="text-left">
                    <h3 className="text-lg font-semibold mb-3">Emerging Differentials</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Conditions identified as emerging differentials during the assessment appointments. Reference these when writing the formulation below.
                    </p>
                    <div className="space-y-4">
                      <ConditionSelectionPanelTOC
                        title="Diagnostic Developmental History"
                        variant="non-report"
                        density="compact"
                        selectedOptions={ssiConditions}
                        isMultiSelect
                        isConfirmed
                        canEdit={false}
                        conditions={{}}
                      />
                      {isObservationEnabled && (
                        <ConditionSelectionPanelTOC
                          title="Behavioural Observation"
                          variant="non-report"
                          density="compact"
                          selectedOptions={obsConditions}
                          isMultiSelect
                          isConfirmed
                          canEdit={false}
                          conditions={{}}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Emerging formulations — side-by-side reference from the two
              appointments (mirrors MDT Review). Drag note cards into the final
              formulation box below rather than retyping from scratch. */}
          <ReviewRow
            title="Emerging Formulation"
            observationEnabled={isObservationEnabled}
            left={
              <NotInReportNotesPanel
                title="Emerging Formulation"
                description="From the interview appointment."
                notes={ssiEmergingNotes}
                canEdit={false}
                allowDragWhileReadOnly={canEdit}
                domainId={SSI_EMERGING_DOMAIN}
              />
            }
            right={
              <NotInReportNotesPanel
                title="Emerging Formulation"
                description="From the observation appointment."
                notes={obsEmergingNotes}
                canEdit={false}
                allowDragWhileReadOnly={canEdit}
                domainId={OBS_EMERGING_DOMAIN}
              />
            }
          />

          {/* Editable final formulation — structured notes (one bullet per note),
              matching every other Autism free-text field. Each note is fed to the
              LLM as its own bullet when the Formulation paragraph is generated. */}
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left">
            <h3 className="text-lg font-semibold mb-1">Final Formulation</h3>
            <p className="text-xs text-gray-400 mb-3">
              Drag notes from the emerging formulations above into this box, or type new notes below.
              Record the final diagnostic formulation that ties together the interview, observation, and decisions above.
            </p>
            <StructuredNotesEditor
              notes={finalFormulationNotes}
              onChange={canEdit ? handleFormulationChange : undefined}
              placeholder="Type a formulation note and press Enter..."
              readOnlyPlaceholder="No formulation recorded"
              readOnly={!canEdit}
              minHeight="10rem"
              domainId="finalFormulationNotes"
              onMoveNoteIn={canEdit ? handleCopyEmergingNoteIntoFinal : undefined}
            />
          </div>
          </div>

          <ModularStepFooter
            showBack
            onBack={handleBack}
            counterText={`${currentSectionIndex + 1} of ${navItems.length}`}
            rightActions={canEdit ? (
              <button
                onClick={handleComplete}
                disabled={!isValid || isSaving}
                title={
                  !wf.skipAutismDifferentialsDecisions && hasOther && !otherComplete
                    ? 'Decide every other condition before completing.'
                    : !hasFormulation
                      ? 'Write the final formulation before completing.'
                      : undefined
                }
                className={`flex items-center gap-2 py-2 px-4 rounded-md shadow-sm transition-all duration-200 text-sm font-semibold whitespace-nowrap ${
                  !isValid || isSaving
                    ? isSaving
                      ? 'opacity-60 cursor-not-allowed bg-primary text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary/90'
                }`}
              >
                {isSaving ? 'Saving...' : 'Complete Diagnostic Decision'}
                {isSaving ? <ButtonSpinner className="w-5 h-5" /> : <CheckCircleIcon className="w-5 h-5" aria-hidden="true" />}
              </button>
            ) : undefined}
          />
        </div>
      </div>

      {/* Confirmation before completing — mirrors TOC ADHD Final Formulation. */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title="Diagnostic Decision Complete"
        description="You have completed the Diagnostic Decision module including the final formulation."
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            <GenerateReportButton
              onClick={handleGenerateReport}
              disabled={isSaving}
              savingLabel={isSaving ? 'Saving...' : undefined}
            />
          </>
        }
      >
        {outcome && (
          <SummarySection title="Decision for Autism:">
            <p className="text-sm text-gray-700">
              Autism Spectrum Disorder (ASD) — <span className="font-semibold">{outcome}</span>
            </p>
          </SummarySection>
        )}
        {hasOther && otherConditionDecisions.length > 0 && (
          <SummarySection title="Decisions for Other Conditions:">
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
              {otherConditionDecisions.map((cond) => (
                <li key={cond.name}>
                  {cond.name}: <span className="font-semibold">{cond.decision}</span>
                </li>
              ))}
            </ul>
          </SummarySection>
        )}
        <DialogMessage>Are you happy to proceed?</DialogMessage>
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="Diagnostic Decision"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={handleAfterGeneration}
        isGenerating={isGenerating}
        onCancel={() => setIsGenerating(false)}
        completionButtonLabel="Go to Documents"
      />
    </Layout>
  );
}
